/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import glamorous from 'glamorous';
import { margin, padding } from 'polished';
import Tappable from 'react-tappable';
import Modal from 'app/components/commons/Modal';
import NameUpdateForm from 'app/components/user/NameUpdateForm';
import ConnectedSocialShareButtons from 'app/components/commons/ConnectedSocialShareButtons';
import { updateUserRealName } from 'app/actions/userActions';
import { IN_PROGRESS } from 'app/constants/challengeStatusTypes';
import * as cssUtils from 'app/utils/cssUtils';
import t from 'app/utils/translate';
import { noOrphanWords } from 'app/utils/stringUtils';

type Props = {
  children: any,
  displayFacebook?: boolean,
  displayTwitter?: boolean,
  displayEmail?: boolean,
  isUserUpdating: boolean,
  onClose: () => void,
  onUserUpdate: () => void,
  showNameForm: boolean,
  title?: string,
  userErrors: Array<*>,
};

const Banner = glamorous.div(props => ({
  backgroundColor: props.theme.color.white,
  color: props.theme.color.basis2,
  ...padding('1rem', '1.5rem'),
}));

const CallToAction = glamorous.div({
  ...padding('0.5rem', null, '0.5rem'),
  textAlign: 'center',
  textTransform: 'uppercase',
});

const CancelButton = glamorous('button', {
  withProps: { type: 'button' },
})(props => ({
  display: 'block',
  fontSize: '1.125rem',
  textTransform: 'uppercase',
  ...margin('0.5rem', 'auto', '0'),
  ...padding('0.25rem'),
  ...cssUtils.hover({
    color: props.theme.color.basis5,
  }),
}));

const Detail = glamorous.div({
  fontSize: '1.125rem',
  ...padding('0.25rem', null, '1.5rem'),
  textAlign: 'center',
});

const ModalContent = glamorous.div({
  borderRadius: 6,
  position: 'relative',
  textAlign: 'center',
});

const NameFormWrapper = glamorous.div(props => ({
  backgroundColor: props.theme.color.white,
  color: props.theme.color.basis2,
  ...padding('0.5rem', '1rem'),
}));

const ShareWrapper = glamorous.div(({ theme }) => ({
  ...padding(null, null, '0.5rem'),
  fontSize: theme.fontSize.large,
}));

const Title = glamorous.div(props => ({
  color: props.theme.color.basis1,
  fontSize: '1.625rem',
  textTransform: 'uppercase',
}));

class ChallengeShareModal extends Component<Props> {
  props: Props;

  static defaultProps = {
    displayFacebook: false,
    displayTwitter: false,
    displayEmail: false,
    title: undefined,
  };

  componentDidUpdate(prevProps: Props) {
    const { userErrors, isUserUpdating, onClose } = this.props;

    if (prevProps.isUserUpdating && !isUserUpdating && userErrors.length === 0) {
      onClose();
    }
  }

  render() {
    const {
      children,
      displayFacebook,
      displayTwitter,
      displayEmail,
      isUserUpdating,
      onClose,
      onUserUpdate,
      showNameForm,
      title,
      userErrors,
    } = this.props;

    return (
      <Modal bare isOpen onCancel={onClose} size="medium">
        <ModalContent>
          <Banner>
            <Title>{title}</Title>
            <Detail>{children}</Detail>
            <CallToAction>{t('Share the joy!')}</CallToAction>
            <ShareWrapper>
              <ConnectedSocialShareButtons
                clipboard
                email={displayEmail}
                facebook={displayFacebook}
                onClose={onClose}
                twitter={displayTwitter}
                vibrant
              />
              <Tappable component={CancelButton} onTap={onClose}>
                {t('Close')}
              </Tappable>
            </ShareWrapper>
          </Banner>
          {showNameForm && (
            <NameFormWrapper>
              <Detail>
                <div>
                  {noOrphanWords(t('Did you know you can search for your friends by name?'))}
                </div>
                <div>
                  {noOrphanWords(t('Add your name below so you can challenge each other.'))}
                </div>
              </Detail>
              <NameUpdateForm
                errors={userErrors}
                isUpdating={isUserUpdating}
                onSubmit={onUserUpdate}
              />
              <Tappable component={CancelButton} onTap={onClose}>
                {t('Continue')}
              </Tappable>
            </NameFormWrapper>
          )}
        </ModalContent>
      </Modal>
    );
  }
}

function mapStateToProps(state, { showNameForm }) {
  const {
    settings: { social = {} },
    user: { currentUser = {}, isSignedIn, updatingUser, errors },
    challenge: { statusId },
  } = state;

  return {
    displayFacebook: social.displayFacebook,
    displayTwitter: social.displayTwitter,
    displayEmail: isSignedIn && statusId < IN_PROGRESS,
    isUserUpdating: updatingUser,
    userErrors: errors,
    showNameForm: !!showNameForm && !currentUser.first_name && !currentUser.last_name,
  };
}

const actions = {
  onUserUpdate: updateUserRealName,
};

export default connect(mapStateToProps, actions)(ChallengeShareModal);
